import type { BlockRecord, RateLimitStore, StoreEntry } from './types'

export interface MemoryStoreOptions {
  /**
   * Maximum unique keys held in memory at once.
   * When the cap is reached the oldest entry is evicted (LRU-style).
   * Set to `0` to disable the cap.
   * @default 10_000
   */
  maxKeys?: number
}

/**
 * Default in-process storage backend.
 *
 * Uses two plain Maps — one for rate-limit trackers, one for block records.
 * Map insertion order provides free LRU eviction: the first key is always
 * the oldest when we need to make room for a new one.
 *
 * Not suitable for multi-process deployments; implement RateLimitStore with
 * a shared backend (Redis, Valkey, …) for that case.
 */
export class MemoryStore implements RateLimitStore {
  private readonly entries = new Map<string, StoreEntry>()
  private readonly blocks = new Map<string, BlockRecord>()
  private readonly maxKeys: number

  constructor({ maxKeys = 10_000 }: MemoryStoreOptions = {}) {
    this.maxKeys = maxKeys
  }

  // ── Tracker ─────────────────────────────────────────────────────────────────

  getEntry(key: string): StoreEntry | null {
    return this.entries.get(key) ?? null
  }

  setEntry(key: string, entry: StoreEntry): void {
    this.evictIfFull(this.entries)
    this.entries.set(key, entry)
  }

  deleteEntry(key: string): void {
    this.entries.delete(key)
  }

  // ── Block ────────────────────────────────────────────────────────────────────

  getBlock(key: string): BlockRecord | null {
    return this.blocks.get(key) ?? null
  }

  setBlock(key: string, record: BlockRecord): void {
    this.blocks.set(key, record)
  }

  deleteBlock(key: string): void {
    this.blocks.delete(key)
  }

  // ── Bulk / lifecycle ─────────────────────────────────────────────────────────

  resetKey(key: string): void {
    this.entries.delete(key)
    this.blocks.delete(key)
  }

  resetAll(): void {
    this.entries.clear()
    this.blocks.clear()
  }

  entryCount(): number {
    return this.entries.size
  }
  blockedCount(): number {
    return this.blocks.size
  }

  cleanup(windowMs: number): void {
    const now = Date.now()

    for (const [key, entry] of this.entries) {
      if (entry.type === 'fixed') {
        if (entry.resetTime <= now) this.entries.delete(key)
      } else {
        const fresh = entry.timestamps.filter(ts => ts > now - windowMs)
        if (fresh.length === 0) {
          this.entries.delete(key)
        } else {
          entry.timestamps = fresh
        }
      }
    }

    for (const [key, block] of this.blocks) {
      if (block.unblockTime <= now) this.blocks.delete(key)
    }
  }

  destroy(): void {
    this.entries.clear()
    this.blocks.clear()
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private evictIfFull(map: Map<string, unknown>): void {
    if (this.maxKeys > 0 && map.size >= this.maxKeys) {
      const oldest = map.keys().next().value as string | undefined
      if (oldest !== undefined) map.delete(oldest)
    }
  }
}
